import { useEffect, useCallback, useRef } from 'react';
import { logger } from '../utils/logger';

interface MultiTabMessage {
  type: 'activity' | 'logout' | 'token_refresh' | 'session_update';
  timestamp: number;
  data?: any;
}

/**
 * Hook for multi-tab coordination using BroadcastChannel
 * Falls back to localStorage events if BroadcastChannel not supported
 */
export const useMultiTabSync = (
  onLogout?: () => void,
  onActivity?: () => void,
  onTokenRefresh?: () => void
) => {
  const channelRef = useRef<BroadcastChannel | null>(null);
  const isInitiatorRef = useRef(false);

  // Send message to other tabs
  const broadcast = useCallback((message: MultiTabMessage) => {
    try {
      if (channelRef.current) {
        channelRef.current.postMessage(message);
        logger.session('Broadcast message sent', { type: message.type });
      } else {
        // Fallback to localStorage
        localStorage.setItem('tab_message', JSON.stringify(message));
        localStorage.removeItem('tab_message'); // Trigger storage event
      }
    } catch (error) {
      logger.error('Failed to broadcast message', error);
    }
  }, []);

  // Broadcast activity
  const broadcastActivity = useCallback(() => {
    broadcast({
      type: 'activity',
      timestamp: Date.now()
    });
  }, [broadcast]);

  // Broadcast logout
  const broadcastLogout = useCallback(() => {
    isInitiatorRef.current = true;
    broadcast({
      type: 'logout',
      timestamp: Date.now()
    });
  }, [broadcast]);

  // Broadcast token refresh
  const broadcastTokenRefresh = useCallback(() => {
    broadcast({
      type: 'token_refresh',
      timestamp: Date.now()
    });
  }, [broadcast]);

  useEffect(() => {
    // Try to use BroadcastChannel (modern browsers)
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        channelRef.current = new BroadcastChannel('app_sync');
        
        channelRef.current.onmessage = (event: MessageEvent<MultiTabMessage>) => {
          const message = event.data;
          
          logger.session('Broadcast message received', { type: message.type });

          switch (message.type) {
            case 'activity':
              if (onActivity) {
                onActivity();
              }
              break;
            
            case 'logout':
              if (onLogout && !isInitiatorRef.current) {
                logger.auth('Logout triggered by another tab');
                onLogout();
              }
              break;
            
            case 'token_refresh':
              if (onTokenRefresh) {
                onTokenRefresh();
              }
              break;
          }
        };

        logger.session('BroadcastChannel initialized');
      } catch (error) {
        logger.error('Failed to initialize BroadcastChannel', error);
      }
    }

    // Fallback: localStorage events
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'tab_message' && e.newValue) {
        try {
          const message: MultiTabMessage = JSON.parse(e.newValue);
          
          logger.session('Storage message received', { type: message.type });

          switch (message.type) {
            case 'activity':
              if (onActivity) {
                onActivity();
              }
              break;
            
            case 'logout':
              if (onLogout && !isInitiatorRef.current) {
                logger.auth('Logout triggered by another tab (storage)');
                onLogout();
              }
              break;
            
            case 'token_refresh':
              if (onTokenRefresh) {
                onTokenRefresh();
              }
              break;
          }
        } catch (error) {
          logger.error('Failed to parse storage message', error);
        }
      }

      // Sync last_activity across tabs
      if (e.key === 'last_activity' && e.newValue) {
        if (onActivity) {
          onActivity();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      if (channelRef.current) {
        channelRef.current.close();
      }
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [onLogout, onActivity, onTokenRefresh]);

  return {
    broadcastActivity,
    broadcastLogout,
    broadcastTokenRefresh
  };
};
